// src/components/customers/AdditionalInfoCards.jsx

/**
 * AdditionalInfoCards Component
 *
 * Displays additional job information in organized card format.
 * Shows comments, plans, products, and other supplementary job details.
 *
 * @param {Object} props - Component props
 * @param {Object} props.job - Job object containing additional information
 * @param {string} props.job.comments - Job comments and notes
 * @param {string} props.job.plan - Job plan details
 * @param {string} props.job.products - Products required for the job
 * @param {string} props.className - Additional CSS classes
 */
const AdditionalInfoCards = ({ job, className = "" }) => {
  const { comments, plan, products } = job || {};

  // Helper function to check if content exists and is not empty
  const hasContent = (content) => {
    return content && content.trim().length > 0;
  };

  // Don't render if no additional information is available
  if (!hasContent(comments) && !hasContent(plan) && !hasContent(products)) {
    return null;
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Job Plan Card */}
      {hasContent(plan) && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="flex items-center p-4 border-b border-gray-200 bg-blue-50">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
              <svg
                className="w-5 h-5 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Job Plan</h3>
              <p className="text-sm text-gray-600">
                Installation plan and approach
              </p>
            </div>
          </div>
          <div className="p-4">
            <div className="prose prose-sm max-w-none">
              <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                {plan}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Products Card */}
      {hasContent(products) && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="flex items-center p-4 border-b border-gray-200 bg-green-50">
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
              <svg
                className="w-5 h-5 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Products Required
              </h3>
              <p className="text-sm text-gray-600">
                Materials and products needed
              </p>
            </div>
          </div>
          <div className="p-4">
            <div className="prose prose-sm max-w-none">
              <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                {products}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Comments Card */}
      {hasContent(comments) && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="flex items-center p-4 border-b border-gray-200 bg-purple-50">
            <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3">
              <svg
                className="w-5 h-5 text-purple-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Comments & Notes
              </h3>
              <p className="text-sm text-gray-600">
                Additional information and observations
              </p>
            </div>
          </div>
          <div className="p-4">
            <div className="prose prose-sm max-w-none">
              <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                {comments}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Summary Card - Shows when multiple sections have content */}
      {[comments, plan, products].filter(hasContent).length > 1 && (
        <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
          <div className="flex items-center text-gray-700">
            <svg
              className="w-5 h-5 mr-2 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="text-sm">
              <span className="font-medium">
                Additional Information Available:
              </span>
              <span className="ml-2">
                {[
                  hasContent(plan) && "Job Plan",
                  hasContent(products) && "Products List",
                  hasContent(comments) && "Comments",
                ]
                  .filter(Boolean)
                  .join(" • ")}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * InfoCard Component
 *
 * A reusable card component for displaying individual pieces of information
 *
 * @param {Object} props - Component props
 * @param {string} props.title - Card title
 * @param {string} props.subtitle - Card subtitle
 * @param {string} props.content - Card content
 * @param {React.ReactNode} props.icon - Icon component
 * @param {string} props.bgColor - Background color class
 * @param {string} props.iconColor - Icon color class
 */
export const InfoCard = ({
  title,
  subtitle,
  content,
  icon,
  bgColor = "bg-gray-50",
  iconColor = "text-gray-600",
}) => {
  if (!content || content.trim().length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      <div
        className={`flex items-center p-4 border-b border-gray-200 ${bgColor}`}
      >
        <div
          className={`w-8 h-8 ${bgColor.replace(
            "50",
            "100"
          )} rounded-full flex items-center justify-center mr-3`}
        >
          <div className={`w-5 h-5 ${iconColor}`}>{icon}</div>
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          {subtitle && <p className="text-sm text-gray-600">{subtitle}</p>}
        </div>
      </div>
      <div className="p-4">
        <div className="prose prose-sm max-w-none">
          <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
            {content}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdditionalInfoCards;
